import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useCloudAuth } from '../../context/CloudAuthContext';

const statusColors = {
  completed: 'bg-green-900/50 text-green-400 border-green-500/50',
  'in-progress': 'bg-blue-900/50 text-blue-400 border-blue-500/50',
  ringing: 'bg-yellow-900/50 text-yellow-400 border-yellow-500/50',
  queued: 'bg-gray-700 text-gray-300 border-gray-500/50',
  failed: 'bg-red-900/50 text-red-400 border-red-500/50',
  'no-answer': 'bg-orange-900/50 text-orange-400 border-orange-500/50',
  busy: 'bg-orange-900/50 text-orange-400 border-orange-500/50',
};

export default function CallDetail() {
  const { callId } = useParams();
  const navigate = useNavigate();
  const { fetchApi } = useCloudAuth();
  const [call, setCall] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadCallData();
  }, [callId]);

  const loadCallData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchApi(`/api/cloud/voice/calls/${callId}`);
      setCall(res.data.call);
      setTranscript(res.data.transcript);
    } catch (err) {
      console.error('Failed to load call:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <svg className="w-8 h-8 animate-spin text-purple-500" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
    );
  }

  if (error || !call) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 bg-red-900/50 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-white mb-2">Call not found</h3>
        <p className="text-gray-400 mb-4">{error || 'The call you are looking for does not exist.'}</p>
        <Link to="/cloud/voice/calls" className="text-purple-400 hover:text-purple-300">
          ← Back to calls
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Back Button */}
      <button
        onClick={() => navigate('/cloud/voice/calls')}
        className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
        data-testid="back-to-calls-btn"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to calls
      </button>

      {/* Call Header */}
      <div className="bg-[#111113] border border-gray-800 rounded-lg p-6 mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-white mb-1 font-mono">
              {call.callId || call._id}
            </h2>
            <p className="text-gray-400 text-sm">{formatDate(call.startedAt)}</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium border ${statusColors[call.status] || 'bg-gray-700 text-gray-300 border-gray-500/50'}`}>
            {call.status}
          </span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <p className="text-gray-500 text-xs uppercase mb-1">Direction</p>
            <p className="text-white flex items-center gap-2">
              {call.direction === 'inbound' ? (
                <>
                  <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14" />
                  </svg>
                  Inbound
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                  Outbound
                </>
              )}
            </p>
          </div>
          <div>
            <p className="text-gray-500 text-xs uppercase mb-1">From</p>
            <p className="text-white">{call.fromNumber}</p>
          </div>
          <div>
            <p className="text-gray-500 text-xs uppercase mb-1">To</p>
            <p className="text-white">{call.toNumber}</p>
          </div>
          <div>
            <p className="text-gray-500 text-xs uppercase mb-1">Duration</p>
            <p className="text-white">{formatDuration(call.durationSec)}</p>
          </div>
          <div>
            <p className="text-gray-500 text-xs uppercase mb-1">Cost</p>
            <p className="text-white">${call.cost?.toFixed(2) || '0.00'}</p>
          </div>
        </div>
      </div>

      {/* Audio Player Placeholder */}
      <div className="bg-[#111113] border border-gray-800 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-medium text-white mb-4">Recording</h3>
        <div className="bg-[#0a0a0b] rounded-lg p-6 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-3 bg-gray-800 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15.536a5 5 0 001.414 1.414m2.828-9.9a9 9 0 0112.728 0" />
              </svg>
            </div>
            <p className="text-gray-400 text-sm">Audio recording is not available for this call</p>
          </div>
        </div>
      </div>

      {/* Transcript */}
      <div className="bg-[#111113] border border-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-medium text-white mb-4">Transcript</h3>
        
        {transcript && transcript.turns && transcript.turns.length > 0 ? (
          <div className="space-y-4">
            {transcript.turns.map((turn, idx) => (
              <div 
                key={idx}
                className={`flex gap-4 ${turn.speaker === 'agent' ? '' : 'flex-row-reverse'}`}
                data-testid={`transcript-turn-${idx}`}
              >
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                  turn.speaker === 'agent' 
                    ? 'bg-purple-900/50 text-purple-400' 
                    : 'bg-blue-900/50 text-blue-400'
                }`}>
                  {turn.speaker === 'agent' ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  )}
                </div>
                <div className={`flex-1 ${turn.speaker === 'agent' ? 'text-left' : 'text-right'}`}>
                  <p className="text-xs text-gray-500 mb-1">
                    {turn.speaker === 'agent' ? 'AI Agent' : 'Caller'} • {formatDate(turn.ts)}
                  </p>
                  <div className={`inline-block px-4 py-2 rounded-lg ${
                    turn.speaker === 'agent'
                      ? 'bg-purple-900/30 text-gray-200'
                      : 'bg-blue-900/30 text-gray-200'
                  }`}>
                    {turn.text}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-[#0a0a0b] rounded-lg p-6 text-center">
            <div className="w-12 h-12 mx-auto mb-3 bg-gray-800 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
            </div>
            <p className="text-gray-400 text-sm">No transcript available for this call</p>
            <p className="text-gray-500 text-xs mt-1">Transcript will be generated when voice AI integration is active</p>
          </div>
        )}
      </div>
    </div>
  );
}
